import { lazy, Suspense } from 'react';
import Navbar from '@/components/smartpos/Navbar';
import Hero from '@/components/smartpos/Hero';
import ManifestoBand from '@/components/smartpos/ManifestoBand';
import { faqs } from '@/components/smartpos/faqData';
import JsonLd from '@/components/smartpos/JsonLd';
import LazySection from '@/components/smartpos/LazySection';
import useDocumentMeta from '@/hooks/useDocumentMeta';
import { useLanguage } from '@/lib/LanguageContext';

const SectorsSection = lazy(() => import('@/components/smartpos/SectorsSection'));
const SuccessCarousel = lazy(() => import('@/components/smartpos/SuccessCarousel'));
const HowItWorksSection = lazy(() => import('@/components/smartpos/HowItWorksSection'));
const VerifactuSection = lazy(() => import('@/components/smartpos/VerifactuSection'));
const GoogleReviews = lazy(() => import('@/components/smartpos/GoogleReviews'));
const FAQSection = lazy(() => import('@/components/smartpos/FAQSection'));
const SeoContentSection = lazy(() => import('@/components/smartpos/SeoContentSection'));
const Footer = lazy(() => import('@/components/smartpos/Footer'));
const FloatingCTA = lazy(() => import('@/components/smartpos/FloatingCTA'));
const SectionIndicator = lazy(() => import('@/components/smartpos/SectionIndicator'));

const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map(f => ({
    '@type': 'Question',
    name: f.question,
    acceptedAnswer: { '@type': 'Answer', text: f.answer }
  }))
};

const breadcrumbJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Inicio', item: 'https://www.smartpos.es/' },
    { '@type': 'ListItem', position: 2, name: 'TPV para Negocio', item: 'https://www.smartpos.es/#tpv-negocio' },
  ],
};

const serviceJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'TPV para negocio — SmartPOS',
  serviceType: 'Sistema TPV / Punto de Venta',
  description: 'Software TPV en la nube, hardware táctil, cajones inteligentes CashDro, quioscos de autoservicio, carta digital y medios de pago integrados. El sistema POS integral para hostelería, retail, alimentación y belleza en España.',
  provider: { '@type': 'Organization', name: 'SmartPOS', url: 'https://www.smartpos.es' },
  areaServed: { '@type': 'Country', name: 'España' },
  url: 'https://www.smartpos.es/',
  offers: {
    '@type': 'Offer',
    price: '49',
    priceCurrency: 'EUR',
    availability: 'https://schema.org/InStock'
  }
};

export default function Home() {
  const { t } = useLanguage();

  useDocumentMeta({
    title: 'TPV para Negocio | Software POS, Hardware y Quioscos — SmartPOS España',
    description: 'TPV para negocio: software TPV en la nube, hardware táctil, CashDro, quioscos y carta digital. El sistema POS integral para hostelería, retail y belleza. Demo gratis.',
    canonicalPath: '/',
  });
  return (
    <div className="min-h-screen font-body">
      <JsonLd data={faqJsonLd} />
      <JsonLd data={breadcrumbJsonLd} />
      <JsonLd data={serviceJsonLd} />
      <Navbar />
      <main id="contenido-principal">
        <Hero />
        <div className="cv-auto">
          <ManifestoBand
            size="lg"
            title={t('home.manifesto1.title')}
          />
        </div>
        <div className="cv-auto">
          <LazySection minHeight={500}>
            <SectorsSection />
          </LazySection>
        </div>
        <div className="cv-auto">
          <LazySection minHeight={160}>
            <SuccessCarousel />
          </LazySection>
        </div>
        <div className="cv-auto">
          <LazySection minHeight={400}>
            <HowItWorksSection />
          </LazySection>
        </div>
        <div id="solucion" className="cv-auto">
          <ManifestoBand
            eyebrow={t('home.manifesto2.eyebrow')}
            variant="solution"
            title={t('home.manifesto2.title')}
          />
        </div>
        <div className="cv-auto">
          <LazySection minHeight={500}>
            <VerifactuSection />
          </LazySection>
        </div>
        <div className="cv-auto">
          <LazySection minHeight={200}>
            <GoogleReviews />
          </LazySection>
        </div>
        <div className="cv-auto">
          <ManifestoBand
            eyebrow={t('home.manifesto3.eyebrow')}
            variant="result"
            title={t('home.manifesto3.title')}
          />
        </div>
        <div id="tpv-negocio" className="cv-auto">
          <LazySection minHeight={400}>
            <SeoContentSection />
          </LazySection>
        </div>
        <div id="faq" className="cv-auto">
          <LazySection minHeight={400}>
            <FAQSection />
          </LazySection>
        </div>
      </main>
      <LazySection minHeight={200}>
        <Footer />
      </LazySection>
      <Suspense fallback={null}>
        <FloatingCTA />
      </Suspense>
      <Suspense fallback={null}>
        <SectionIndicator />
      </Suspense>
    </div>
  );
}